'use client';

import { useState, useMemo } from 'react';

import Image from 'next/image';

import { motion } from 'framer-motion';
import { ArrowRight, Lock, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useTranslation } from '@/lib/i18n/context';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CourseUnlockModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseTitle: string;
  courseImage?: string;
}

// Same elegant styles as the course cards
const CARD_STYLES = [
  {
    font: 'font-cormorant',
    gradient: 'from-amber-900/60',
    spot: 'bg-amber-500/60',
    spotPosition: 'bottom-[15%] -right-8',
  },
  {
    font: 'font-eb-garamond',
    gradient: 'from-rose-900/55',
    spot: 'bg-rose-500/55',
    spotPosition: 'bottom-[20%] -left-8',
  },
  {
    font: 'font-source-serif',
    gradient: 'from-emerald-900/55',
    spot: 'bg-emerald-500/55',
    spotPosition: 'bottom-[10%] -right-6',
  },
  {
    font: 'font-noto-serif',
    gradient: 'from-violet-900/50',
    spot: 'bg-violet-500/60',
    spotPosition: 'bottom-[15%] -left-10',
  },
  {
    font: 'font-cormorant',
    gradient: 'from-cyan-900/55',
    spot: 'bg-cyan-500/55',
    spotPosition: 'bottom-[20%] -right-8',
  },
];

export function CourseUnlockModal({
  open,
  onOpenChange,
  courseTitle,
  courseImage = 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&q=80&w=800',
}: CourseUnlockModalProps) {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    teamSize: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pick a random style on mount
  const style = useMemo(
    () => CARD_STYLES[Math.floor(Math.random() * CARD_STYLES.length)],
    [],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Here you would typically send this data to your backend
    console.log('Course unlock data:', {
      ...formData,
      course: courseTitle,
    });

    // Close modal and redirect or show success
    setIsSubmitting(false);
    onOpenChange(false);

    // You could redirect to the course content or show a success message
    alert(
      isAr
        ? `سجّلناك في قائمة الانتظار لدورة ${courseTitle}. سنُعلمك فور إطلاقها.`
        : `You're on the waitlist for ${courseTitle}! We'll notify you when it launches. 🎉`,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-[500px]">
        {/* Hero Section - Netflix/Masterclass Style */}
        <div className="relative h-56 w-full overflow-hidden bg-neutral-900">
          {/* Background Image */}
          <Image
            src={courseImage}
            alt=""
            aria-hidden
            fill
            unoptimized
            className="object-cover"
          />

          {/* Colorful spot accents - like the cards */}
          <div
            className={`absolute ${style.spotPosition} h-40 w-40 rounded-full ${style.spot} blur-2xl`}
          />
          <div
            className={`absolute bottom-[5%] left-1/4 h-28 w-28 rounded-full ${style.spot} opacity-50 blur-xl`}
          />

          {/* Gradient Overlays - matching card style */}
          <div
            className={`absolute inset-0 bg-gradient-to-t ${style.gradient} via-black/60 to-transparent`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black from-0% via-black/80 via-35% to-transparent to-70%" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" />

          {/* Top Badge */}
          <div className="absolute top-5 left-5">
            <span className="rounded-sm bg-white/90 px-3 py-1 text-[10px] font-medium tracking-widest text-neutral-800">
              {isAr ? t.academy['Open Academy'] : 'OPEN ACADEMY'}
            </span>
          </div>

          {/* Content - Bottom aligned like cards */}
          <div className="absolute inset-x-0 bottom-0 flex h-full flex-col justify-end p-6 text-left">
            {/* Category Label */}
            <span className="mb-2 text-[11px] font-normal uppercase tracking-[0.2em] text-white/60">
              {isAr ? 'قريباً' : 'Coming Soon'}
            </span>

            <DialogHeader className="space-y-1 text-left">
              <DialogTitle
                className={`${style.font} text-[1.75rem] font-normal leading-[1.1] tracking-tight text-white`}
              >
                {isAr ? 'احجز مكانك في قائمة الانتظار' : 'Join the Waitlist'}
              </DialogTitle>
              <DialogDescription
                className={`${style.font} text-[1.3rem] font-normal leading-[1.15] text-white/85`}
              >
                {isAr ? 'كن أول من يعرف' : 'Be the first to know'}
              </DialogDescription>
            </DialogHeader>

            {/* Decorative line */}
            <div className="mt-3 flex items-center gap-3">
              <span className="h-px w-4 bg-white/40" />
              <span className="font-cormorant text-[13px] font-normal italic text-white/70">
                {isAr
                  ? 'نُعلمك فور إطلاق الدورة'
                  : 'Get notified when this course launches'}
              </span>
            </div>
          </div>

          {/* Subtle border overlay */}
          <div className="absolute inset-0 ring-1 ring-white/5 ring-inset" />
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="space-y-5 p-6">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              {isAr ? 'الاسم' : 'Your name'}
            </Label>
            <Input
              id="name"
              type="text"
              placeholder={isAr ? 'محمد العتيبي' : 'John Doe'}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              className="h-11"
            />
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              {isAr ? 'الإيميل' : 'Email address'}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="john@company.com"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
              className="h-11"
            />
          </div>

          {/* Team Size Field */}
          <div className="space-y-2">
            <Label htmlFor="teamSize" className="text-sm font-medium">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                {isAr ? 'كم حجم فريقك؟' : 'How big is your team?'}
              </div>
            </Label>
            <Select
              value={formData.teamSize}
              onValueChange={(value) =>
                setFormData({ ...formData, teamSize: value })
              }
              required
            >
              <SelectTrigger className="h-11">
                <SelectValue
                  placeholder={isAr ? 'اختر حجم الفريق' : 'Select team size'}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="solo">
                  {isAr ? 'أنا بس' : 'Just me'}
                </SelectItem>
                <SelectItem value="2-5">
                  {isAr ? '٢-٥ أشخاص' : '2-5 people'}
                </SelectItem>
                <SelectItem value="6-10">
                  {isAr ? '٦-١٠ أشخاص' : '6-10 people'}
                </SelectItem>
                <SelectItem value="11-50">
                  {isAr ? '١١-٥٠ شخصاً' : '11-50 people'}
                </SelectItem>
                <SelectItem value="51-200">
                  {isAr ? '٥١-٢٠٠ شخص' : '51-200 people'}
                </SelectItem>
                <SelectItem value="200+">
                  {isAr ? '+٢٠٠ شخص' : '200+ people'}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col gap-3 pt-1">
            <Button
              type="submit"
              size="lg"
              className="h-11 w-full text-base font-medium"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: 'linear',
                    }}
                  >
                    <Lock className="mr-2 h-4 w-4" />
                  </motion.div>
                  {isAr ? 'جارٍ التسجيل...' : 'Joining...'}
                </>
              ) : (
                <>
                  {isAr ? t.academy['Join Waitlist'] : 'Join Waitlist'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
            <p className="text-muted-foreground text-center text-xs">
              {isAr
                ? 'سنُعلمك فور إطلاق هذه الدورة.'
                : "We'll notify you when this course launches."}
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
